import { ClueyLogoBase, SiteTitle } from '@cluey/cluey-components';
import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, generatePath, useHistory } from 'react-router-dom';

import { userSignout } from '../../actions/hub/hubAuthPagesActions';
import { api } from '../../api';
import { AccountDetails } from '../../api/types/account';
import Cross from '../../assets/images/atoms-icon-filled-cross.svg';
import Borger from '../../assets/images/atoms-icon-filled-hamburger.svg';
import ClueyLogo from '../../assets/images/cluey-logo.svg';
import { useSelectEnrolmentModal } from '../../components/Modals/SelectEnrolmentModal/SelectEnrolmentModal';
import env from '../../env';
import { getActiveEnrolments } from '../../selectors/hubSelector';
import { Enrolment } from '../../types/hubInterfaces';
import {
  PATH_HUB_ACCOUNT_DETAILS,
  PATH_HUB_ADD_ONE_OFF_SESSION,
  PATH_HUB_HOME,
  PATH_HUB_PLAN_DETAILS,
  PATH_HUB_SESSIONS_PAGE,
  PATH_HUB_SUBSCRIPTIONS,
  PATH_HUB_SUPPORT,
  PATH_HUB_VOUCHERS,
} from '../../util/pagePath';

export interface HubHeaderProps {
  title?: string;
  hideMenu?: boolean;
  onLogoClick?: () => void;
}

interface NavItem {
  label: string;
  path?: string;
  onClick?: () => void;
  hidden?: boolean;
  children?: Omit<NavItem, 'children' | 'hidden'>[];
}

const HubHeader = ({ title, hideMenu = false, onLogoClick }: HubHeaderProps) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const menuOnHover = () => setDropdownOpen(true);
  const menuOnHoverExit = () => setDropdownOpen(false);
  const toggleMenu = () => setDropdownOpen(!dropdownOpen);

  const history = useHistory();
  const { data: details } = api.account.details.useQuery();
  const { data: subscription } = api.subscription.checkSubscription.useQuery();
  const activeEnrolments: Array<Enrolment> = useSelector(getActiveEnrolments);
  const { open, openModal, closeModal, SelectEnrolmentModal } = useSelectEnrolmentModal();

  const dropdownRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const signOut = () => {
    dispatch(userSignout());
  };

  const handleBookSession = useCallback(() => {
    if (activeEnrolments.length === 1) {
      const enrolmentId = activeEnrolments[0].enrolmentId;
      history.push(
        generatePath(PATH_HUB_ADD_ONE_OFF_SESSION, {
          enrolmentId,
        })
      );
    } else {
      openModal();
    }
  }, [activeEnrolments, history, openModal]);

  const handleStudentHubClick = useCallback(() => {
    window.open(env.REACT_APP_STUDENT_HUB, '_blank');
  }, []);

  const NAV_ITEMS: Record<string, NavItem> = useMemo(
    () => ({
      Home: {
        label: 'Home',
        path: PATH_HUB_HOME,
      },
      Sessions: {
        label: 'Sessions',
        path: PATH_HUB_SESSIONS_PAGE,
      },
      BookSession: {
        label: 'Book a session',
        onClick: handleBookSession,
        hidden: activeEnrolments.length === 0 || !activeEnrolments.some((enrolment) => enrolment.type === 'Course'),
      },
      Vouchers: {
        label: 'Cluey Vouchers',
        path: PATH_HUB_VOUCHERS,
      },
      Subscriptions: {
        label: 'Subscriptions',
        path: PATH_HUB_SUBSCRIPTIONS,
      },
      Account: {
        label: 'Account',
        path: PATH_HUB_PLAN_DETAILS,
      },
      Support: {
        label: 'Support',
        path: PATH_HUB_SUPPORT,
      },
      StudentHub: {
        label: 'Student Hub',
        onClick: handleStudentHubClick,
        hidden: !subscription?.hasValidSubscription,
      },
    }),
    [activeEnrolments, handleBookSession, handleStudentHubClick, subscription?.hasValidSubscription]
  );

  const getNavOptions = useCallback(
    (userType: AccountDetails['userType'] | null) => {
      if (userType === 'Account') {
        return Object.values(NAV_ITEMS).filter((item) => !item.hidden);
      } else {
        return [
          NAV_ITEMS.Home,
          {
            ...NAV_ITEMS.Account,
            path: PATH_HUB_ACCOUNT_DETAILS,
          },
        ];
      }
    },
    [NAV_ITEMS]
  );

  const navOptions = useMemo(() => getNavOptions(details?.userType), [details?.userType, getNavOptions]);

  useEffect(() => {
    const closeMenu = (e: MouseEvent) => {
      if (dropdownOpen && dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };

    if (dropdownOpen) {
      document.addEventListener('click', closeMenu);
    }
    return () => {
      document.removeEventListener('click', closeMenu);
    };
  }, [dropdownOpen]);

  const renderLogoutButton = (
    <button
      className="flex h-10 items-center px-4 text-base font-bold tracking-wider text-blue-5 hover:bg-white"
      onClick={signOut}
    >
      Log out
    </button>
  );

  const renderMenuButton = (
    <div
      className="flex h-full items-center lg:relative"
      ref={dropdownRef}
      onMouseEnter={menuOnHover}
      onMouseLeave={menuOnHoverExit}
    >
      <button
        className={classNames(
          'absolute right-0 top-0 flex h-14 w-14 cursor-pointer items-center gap-2 px-4 lg:static lg:h-full lg:w-auto lg:hover:bg-slate-1',
          {
            'bg-slate-1': dropdownOpen,
          }
        )}
        onClick={toggleMenu}
      >
        <img src={Borger} alt="" className={dropdownOpen ? 'hidden lg:block' : 'block'} />
        <img src={Cross} alt="" className={dropdownOpen ? 'block lg:hidden' : 'hidden'} />
        <span className="hidden font-bold text-blue-5 lg:block">Menu</span>
      </button>
      <nav
        className={classNames(
          'absolute left-0 right-0 top-[calc(100%+4px)] z-20 flex h-screen w-screen flex-col bg-slate-1 pb-5 pt-4 lg:left-auto lg:top-full lg:h-auto lg:w-56',
          {
            hidden: !dropdownOpen,
          }
        )}
      >
        {navOptions.map((option) => {
          if (option.path) {
            return (
              <React.Fragment key={option.path}>
                <Link
                  to={option.path}
                  className="flex h-10 items-center px-4 text-base font-bold tracking-wider text-blue-5 hover:bg-white"
                >
                  {option.label}
                </Link>
                {option.children && (
                  <div className="flex flex-col">
                    {option.children.map((child) => (
                      <Link
                        key={child.path ?? child.label}
                        to={child.path}
                        className="flex h-10 items-center px-4 pl-8 text-sm font-bold tracking-wider text-blue-5 hover:bg-white"
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                )}
              </React.Fragment>
            );
          }
          return (
            <button
              key={option.label}
              onClick={option.onClick}
              className="flex h-10 items-center px-4 text-left text-base font-bold tracking-wider text-blue-5 hover:bg-white"
            >
              {option.label}
            </button>
          );
        })}
        <hr className="my-2 border-t border-grey-3" />
        {renderLogoutButton}
      </nav>
    </div>
  );

  return (
    <div className="sticky top-0 z-30 flex h-14 w-full border-b-4 border-blood-orange-4 bg-white lg:h-20">
      <header className="container mx-auto flex items-center justify-between lg:px-4">
        <div className="flex items-center px-4 lg:px-0">
          <Link to={PATH_HUB_HOME} onClick={onLogoClick}>
            <ClueyLogoBase src={ClueyLogo} />
          </Link>
          <span className="hidden">
            <SiteTitle title={title} />
          </span>
        </div>
        {hideMenu ? renderLogoutButton : renderMenuButton}
      </header>
      <SelectEnrolmentModal
        position="top"
        open={open}
        closeModal={closeModal}
        enrolmentLinkTo={(enrolment) => {
          return generatePath(PATH_HUB_ADD_ONE_OFF_SESSION, {
            enrolmentId: enrolment.enrolmentId,
          });
        }}
        subtitle="Which enrolment do you need to book a make-up session for?"
        filterEnrolmentType="Course"
      />
    </div>
  );
};

export default HubHeader;
